import { formatBuildingAddress } from "src/model/class/general classes/building-address";
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import { ActionSheetController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { filter, map } from "rxjs/operators";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { Environment } from "src/components/shared-components/class/general-classes/environment";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { Supervision } from "src/model/class/general classes/supervision";
import { ActionListBuilding } from "src/model/enum/enumActionSheetOperation";
import {
  BACSCoefficient,
  EnergyCoefficient,
  UsersRole,
} from "src/model/enum/enumDigitalPlace";
import { ApplicationTheme } from "src/components/shared-components/enum/enumMisc";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { AlertService } from "src/service/alert.service";
import { DpBuildingService } from "src/service/building.service";
import { EnvironmentService } from "src/service/environment.service";
import { EventService } from "src/service/event.service";
import { NetworkService } from "src/service/network.service";
import { PlantService } from "src/service/plant.service";
import { SupervisionService } from "src/service/supervision.service";
import { SystemRouterService } from "src/service/system-router.service";
import { ThemeService } from "src/service/theme.service";
import { currentBuildingChangeSelectionAction } from "src/components/shared-components/state/actions/building.action";
import { AreaService } from "src/service/area.service";

@Component({
    selector: "app-building",
    templateUrl: "./building.component.html",
    styleUrls: ["./building.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

/**
 * Building single entry componet
 */
export class BuildingComponent implements OnInit, OnDestroy, iTranslation {
  private _translateSubscription: Subscription = null;

  private _lblDelete: string = "";
  private _lblClose: string = "";
  private _lblDeleteBuilding: string = "";
  private _networkServiceSubscription: Subscription = null;
  private _lblEditData: string = "";
  private _lblViewOnMap: string = "";
  private _lblUserManagement: string = "";
  private _lblEnvironmentsManagement: string = "";
  private _lblSelectAction: string = "";
  private _lblManageUserAction: string = "";
  private _lblManageEnvironmentsAction: string = "";
  private _lblDeleteAction: string = "";
  private _lblUpdateAction: string = "";
  private _lblDeletePartsAndSupervision: string = "";
  private _genericListEnvironments: GenericApiData = null;
  private _listSupervision: Array<Supervision> = null;
  private _customInterface: Supervision = null;

  public displayLines: string = "start";
  public isBuildingAdministrator: boolean = false;
  public hasEnergyCoefficient: boolean = false;
  public hasBACS: boolean = false;
  public listEnvironments: Array<Environment> = new Array();

  @Input() currentBuilding: Building;
  @Input() hideMenu: boolean = false;
  @Input() hideLines: boolean = false;
  @Input() role: UsersRole;
  @Output() reloadBuildings = new EventEmitter();
  @Output() selectBuilding = new EventEmitter<number>();
  /** Show the "show on map" button (the list shows it only when its map is on screen) */
  @Input() showLocateButton: boolean = false;
  /** Highlight the row (the building picked on the map) */
  @Input() selected: boolean = false;

  constructor(
    private _eventService: EventService,
    private _plantService: PlantService,
    private _alertService: AlertService,
    private _translateService: TranslateService,
    private _systemRouterService: SystemRouterService,
    private _environmentService: EnvironmentService,
    private _networkService: NetworkService,
    private _themeService: ThemeService,
    private _actionSheetCtrl: ActionSheetController,
    private _buildingService: DpBuildingService,
    private _store: Store,
    private _supervisioneService: SupervisionService,
    private _areaService: AreaService
  ) {
    //this.translationSub();
  }

  ngOnInit() {
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeSupervision();
  }

  ngOnChanges() {
    if (this.hideLines) {
      this.displayLines = "none";
    }
    if (this.role == UsersRole.BUILDING_ADMINISTRATOR) {
      this.isBuildingAdministrator = true;
    } else {
      this.isBuildingAdministrator = false;
    }
    if (this.currentBuilding?.BACSCoefficient) {
      this.hasBACS = true;
    }
    if (this.currentBuilding?.energyCoefficient) {
      this.hasEnergyCoefficient = true;
    }
  }

  ngOnDestroy() {
    this._translateSubscription.unsubscribe();
  }

  /**
   * Subscribe to supervision
   */
  public subscribeSupervision() {
    this._supervisioneService.listSupervision
      .pipe(
        filter((supervision) => supervision !== null),
        map((listSupervision: Array<any>) => {
          const supervisionTypedList: Supervision[] = listSupervision.map(
            (supervision) => new Supervision(supervision)
          );

          return supervisionTypedList;
        })
      )
      .subscribe((lSupervision: Array<Supervision>) => {
        this._listSupervision = lSupervision;
      });
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.delete",
        "dictionary.close",
        "dictionary.editData",
        "dictionary.confirmDeleteBuilding",
        "dictionary.userManagement",
        "dictionary.manageEnvironment",
        "dictionary.manageBuildingParts",
        "dictionary.select",
        "dictionary.viewOnMap",
        "dictionary.descriptionSelectBuildingMaps",
        "dictionary.descriptionManageUser",
        "dictionary.descriptionManageEnvironmentsParts",
        "dictionary.descriptionDeleteBuilding",
        "dictionary.descriptionUpdateBuilding",
        "dictionary.deletePartsAndSupervision",
      ])
      .subscribe((translation) => {
        this._lblDelete = translation["dictionary.delete"];
        this._lblClose = translation["dictionary.close"];
        this._lblEditData = translation["dictionary.editData"];
        this._lblViewOnMap = translation["dictionary.viewOnMap"];
        this._lblUserManagement = translation["dictionary.userManagement"];
        this._lblDeleteBuilding =
          translation["dictionary.confirmDeleteBuilding"];
        this._lblEnvironmentsManagement =
          translation["dictionary.manageBuildingParts"];
        this._lblSelectAction =
          translation["dictionary.descriptionSelectBuildingMaps"];
        this._lblManageUserAction =
          translation["dictionary.descriptionManageUser"];
        this._lblManageEnvironmentsAction =
          translation["dictionary.descriptionManageEnvironmentsParts"];
        this._lblDeleteAction =
          translation["dictionary.descriptionDeleteBuilding"];
        this._lblUpdateAction =
          translation["dictionary.descriptionUpdateBuilding"];
        this._lblDeletePartsAndSupervision =
          translation["dictionary.deletePartsAndSupervision"];
      });
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   *
   * @param event dom event ref
   * @param idCurrentPlant id plant clicked
   * @param item item sliding event ref
   *
   * Perform redirect to plant edit page
   *
   */
  public async onClickOpenActionSheet(event: any): Promise<void> {
    event.stopPropagation();
    event.preventDefault();
    let arFields: ActionListBuilding[] = [];
    arFields.unshift(ActionListBuilding.DELETE);
    arFields.unshift(ActionListBuilding.MANAGE_ENVIRONMENTS);
    arFields.unshift(ActionListBuilding.MANAGE_USER);
    arFields.unshift(ActionListBuilding.UPDATE);
    arFields.unshift(ActionListBuilding.SELECT);
    const actionSheet = await this._actionSheetCtrl.create({
      buttons: [
        {
          icon: "pin-outline",
          data: {
            action: ActionListBuilding.SELECT,
          },
        },

        {
          icon: "create-outline",
          data: {
            action: ActionListBuilding.UPDATE,
          },
        },
        {
          icon: "people",
          data: {
            action: ActionListBuilding.MANAGE_USER,
          },
        },
        {
          icon: "layers-outline",
          data: {
            action: ActionListBuilding.MANAGE_ENVIRONMENTS,
          },
        },
        {
          icon: "trash-outline",
          data: {
            action: ActionListBuilding.DELETE,
          },
        },
        {
          icon: "close",
          text: this._lblClose,
        },
      ],
    });

    for (let i = 0; i < arFields.length; i++) {
      const label = document.createElement("ion-label");

      label.innerHTML =
        "<p style='font-size: 14px;margin-bottom:-1px;margin-top:2px;color:black'> " +
        this.getTitleActionSheet(arFields[i]) +
        "</p><p style='font-size: 11px;color:gray;line-height:1.2'>" +
        this.getSubtitleActionSheet(arFields[i]) +
        "</p>";

      const buttonEl = actionSheet.querySelectorAll(
        ".action-sheet-button-inner"
      );
      buttonEl[i].appendChild(label);
    }

    actionSheet.present();
    let result = await actionSheet.onDidDismiss();
    switch (result.data?.action) {
      case ActionListBuilding.UPDATE:
        this._systemRouterService.navigateToBuildingEdit(
          this.currentBuilding.id
        );
        break;
      case ActionListBuilding.DELETE:
        this.performDeleteBuilding();
        break;
      case ActionListBuilding.MANAGE_USER:
        this._systemRouterService.navigateToInviteUser(this.currentBuilding.id);
        break;
      case ActionListBuilding.SELECT:
        this.selectBuilding.emit(this.currentBuilding.id);
        break;
      case ActionListBuilding.MANAGE_ENVIRONMENTS:
        this._systemRouterService.navigateToDigitalPlaceManageEnvironments(
          this.currentBuilding.idAreaLocal
        );
        break;
    }
  }

  /**
   * Get the subtitle for action sheet for card
   * @param typeAction
   * @returns
   */
  private getSubtitleActionSheet(typeAction: ActionListBuilding) {
    let subTitle: string = "";
    if (typeAction == ActionListBuilding.SELECT) {
      subTitle = this._lblSelectAction;
    }
    if (typeAction == ActionListBuilding.UPDATE) {
      subTitle = this._lblUpdateAction;
    }
    if (typeAction == ActionListBuilding.MANAGE_USER) {
      subTitle = this._lblManageUserAction;
    }
    if (typeAction == ActionListBuilding.MANAGE_ENVIRONMENTS) {
      subTitle = this._lblManageEnvironmentsAction;
    }
    if (typeAction == ActionListBuilding.DELETE) {
      subTitle = this._lblDeleteAction;
    }
    return subTitle;
  }

  /**
   * Get the title of action sheet for card
   * @param typeAction
   * @param lblNotify
   * @returns
   */
  private getTitleActionSheet(typeAction: ActionListBuilding) {
    let title: string = "";
    if (typeAction == ActionListBuilding.SELECT) {
      title = this._lblViewOnMap;
    }
    if (typeAction == ActionListBuilding.UPDATE) {
      title = this._lblEditData;
    }
    if (typeAction == ActionListBuilding.MANAGE_USER) {
      title = this._lblUserManagement;
    }
    if (typeAction == ActionListBuilding.MANAGE_ENVIRONMENTS) {
      title = this._lblEnvironmentsManagement;
    }
    if (typeAction == ActionListBuilding.DELETE) {
      title = this._lblDelete;
    }

    return title;
  }

  /**
   * Get label of energy coefficient
   * @param value
   * @returns
   */
  public getEnergyCoefficient(value): string {
    return EnergyCoefficient[value];
  }

  /**
   * Get label of BACS coefficient
   */
  public getBACSCoefficient(value): string {
    return BACSCoefficient[value];
  }

  /**
   * Perform plant deleting, after user request confirm
   *
   * @param event dom event ref
   * @param idCurrentPlant id plant clicked
   * @param item item sliding event ref
   */
  public async performDeleteBuilding(): Promise<void> {
    try {
      const cancelOperation: boolean =
        await this._alertService.presentAlertConfirmOrDismiss(
          this._lblDeleteBuilding,
          this._lblDeletePartsAndSupervision,
          ""
        );

      if (cancelOperation) {
        //await this._buildingService.deleteBuildingbyID(this.currentBuilding.id);
        await this._buildingService.deRegisterBuilding(this.currentBuilding);
        await this.deletePartOfBuilding();
        this.reloadBuildings.emit();
      }
    } catch (exception) {
      await this._alertService.presentAlertSomethingWrong();
    }
  }

  /**
   * Delete part of building
   */
  public async deletePartOfBuilding() {
    this._genericListEnvironments = await this._environmentService
      .getEnvironments(this.currentBuilding.idAreaLocal, null, false)
      .toPromise();
    this.listEnvironments = this._genericListEnvironments.data;
    for (let i = 0; i < this.listEnvironments.length; i++) {
      this._environmentService.deleteEnvironment(this.listEnvironments[i].id);
      this._customInterface = this._listSupervision.find(
        (item) => item.idEnvironment === this.listEnvironments[i].id
      );
      if (this._customInterface) {
        this._supervisioneService.deleteSupervision(this._customInterface.id);
      }
    }
  }

  /**
   * Perform click building event: it redirect to login access form
   *
   * @param clickedBuilding current clicked plants
   */
  public async onClickBuilding(clickedBuilding: Building): Promise<void> {
    try {
      let area = await this._areaService.getAreaById(
        clickedBuilding.idAreaLocal
      );
      this._store.dispatch(
        currentBuildingChangeSelectionAction({
          selectedBuilding: clickedBuilding,
          defaultBuilding: area.default,
        })
      );
      this._supervisioneService.reloadServiceData();
      this._systemRouterService.navigateToDigitalPlaceHome();
    } catch {
      this._alertService.presentAlertSomethingWrong();
    }
  }

  /**
   * The building has coordinates, so it has a marker on the map
   */
  public get hasCoordinates(): boolean {
    const latitude = this.currentBuilding?.latitude;
    const longitude = this.currentBuilding?.longitude;
    return (
      latitude !== null && latitude !== undefined && `${latitude}` !== "" &&
      longitude !== null && longitude !== undefined && `${longitude}` !== "" &&
      !isNaN(Number(latitude)) && !isNaN(Number(longitude))
    );
  }

  /**
   * Centre the map on this building (does not open it, unlike a tap on the row)
   */
  public onClickLocate(event: Event): void {
    event.stopPropagation();
    this.selectBuilding.emit(this.currentBuilding.id);
  }

  /**
   * Address of the building: "Via Roma 1, Torino (TO)", skipping the missing parts
   *
   * @param building building to describe
   * @returns the address, empty when the building has none
   */
  public formatAddressLabel(building: Building): string {
    return formatBuildingAddress(building);
  }

}
